import {Trans, useTranslation} from "next-i18next";
import {useEffect, useState} from "react";
import {useUser} from "@/base/components/auth/UserProvider";
import {withCache} from "@/base/utils/cache";
import {ModifyURL} from "@/base/utils/fetch";
import {useNotification} from "@/base/components/NotificationProvider";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import MoonFilled from "@ant-design/icons/MoonFilled";
import StarFilled from "@ant-design/icons/StarFilled";
import {format} from "date-fns/format";
import useFNSLocale from "@/base/utils/datefnslocale";

export type SupportWorkingHoursProps = {
	className?: string;
	children?: React.ReactNode;
	onDataUpdate?: (data?: SupportWorkingHoursData) => void;
	hidden?: boolean;
}

export interface SupportWorkingHoursData
{
	hours: number;
	from: string;
	to: string;
	in_hours: {
		from: number;
		to: number;
	};
}

export const SupportWorkingHours = ({
	className = ``,
	children = null,
	onDataUpdate = () => {
	},
	hidden = false
}: SupportWorkingHoursProps) => {
	const {t} = useTranslation();
	const UserContext = useUser();
	const [api] = useNotification();
	const fnsLocale = useFNSLocale();

	const [data, setData] = useState<SupportWorkingHoursData | undefined>(undefined);

	const getWorkingHours = async (): Promise<SupportWorkingHoursData | undefined> => {
		return (await withCache(
			'support_working_hours',
			60 * 20 * 1000,
			() => UserContext.Fetch({
				url: ModifyURL(`/support/working-hours`),
			}).then((res: {
				data?: SupportWorkingHoursData
			}) => {
				if (!res?.data || !res?.data?.in_hours)
				{
					throw new Error("Invalid support data");
				}

				return res.data;
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		)) || undefined;
	}

	useEffect(() => {
		getWorkingHours().then(data => {
			setData(data);
			onDataUpdate(data);
		});
	}, []);

	const getDateFrom = (): Date | undefined => {
		return data?.from ? new Date(data.from) : undefined;
	}

	const getDateTo = (): Date | undefined => {
		return data?.to ? new Date(data.to) : undefined;
	}

	const getNextStartWorkingTime = (): Date => {
		if (isWorking())
		{
			return new Date();
		}

		// if expired and dateFrom not this day add one day
		const dateFrom = getDateFrom();
		const dateTo = getDateTo();

		if (!dateFrom || !dateTo)
		{
			return new Date();
		}

		const now = new Date();

		if (now > dateTo)
		{
			dateFrom.setDate(now.getDate() + (now.getDate() !== dateFrom.getDate() ? 0 : 1));
		}

		return dateFrom;
	}

	const isWorking = () => {
		const startDate = getDateFrom();
		const endDate = getDateTo();

		if (!startDate || !endDate)
		{
			return true;
		}

		const now = new Date();

		return now >= startDate && now <= endDate;
	}

	const nextOpenHour = getNextStartWorkingTime();

	return !hidden && data && !isWorking() && <div className={`bg-amber-300/20 w-fit !text-start overflow-hidden flex flex-col rounded-lg relative ${className}`}>
		<MoonFilled className={`absolute !text-[100px] !left-[5%] !top-[0%] !bottom-[0%] z-0 !text-text-2/5`}/>
		<StarFilled className={`absolute !text-[30px] !left-[20%] !top-[5%] z-0 !text-color/10`}/>
		<StarFilled className={`absolute !text-[20px] !left-[27%] !top-[24%] z-0 !text-color/5`}/>
		<div className={`flex items-center gap-4 bg-amber-400/30 py-4 px-6 relative`}>
			<ClockCircleOutlined className={`!text-text-2 text-2xl`}/>
			<p className={`text-lg font-semibold text-text-2`}>
				{t(`base-support:working-hour.titles`)}
			</p>
		</div>
		<div className={`py-4 px-6 relative z-[1]`}>
			<p className={`!mt-0`}>
				<Trans
					i18nKey={`base-support:working-hour.description`}
					values={{
						'from': format(nextOpenHour, nextOpenHour.getDate() == (new Date()).getDate() ? 'HH:mm' : 'dd MMMM yyyy HH:mm', {locale: fnsLocale}),
					}}
					components={{
						B: <span className={`font-semibold`}/>
					}}
				/>
			</p>
		</div>
	</div>
}

export default SupportWorkingHours;